import {
  GERMAN_REGIONS,
  type Region,
} from '@schuelerhilfe/shared/src/geo/bundeslaender';
import type { DomainKey } from './domain-config';

// The German region list itself lives in `@schuelerhilfe/shared` because the
// backend's sitemap generator emits the same `/{bundesland}/` URLs this file
// routes — see the note there. Re-exported so consumers of this module keep
// one import.
export type { Region };

/**
 * Target page for a region. State Aggregate Pages sit at root level, NOT under
 * `/nachhilfe/`. The trailing slash is required: `trailingSlash: true` in
 * next.config.ts, so omitting it costs a 308 hop on every crawl.
 */
export const regionHref = (region: Region): string =>
  region.usesCityListing
    ? `/standorte/${region.slug}-nachhilfe/`
    : `/${region.slug}/`;

export type Country = {
  id: string;
  name: string;
  /**
   * Key of the GeoJSON asset at `public/{mapKey}-states.json`. Set ONLY once
   * that file ships: `CountryMap` fetches it asynchronously, so a missing asset
   * cannot be detected at render time and its presence is encoded here instead
   * (10.03.03.14 § AC 5).
   */
  mapKey?: string;
  regions: Region[];
};

/** A country with both a region list and a shipped map asset — safe to render. */
export type MappedCountry = Country & { mapKey: string };

const DOMAIN_TO_COUNTRY_ID: Record<string, string> = {
  de: 'DE',
  at: 'AT',
  ch: 'CH',
  ien: 'AT',
};

// 10.03.03.14 § AC 5. Deliberately no default-country fallback — rendering the
// German map on an AT or CH domain is a defect. A country qualifies only with
// BOTH a region list and a shipped map asset.
//
// The brand comes in as a parameter rather than from `getActiveDomainKey()`:
// this module is reached from a client component, where `process.env.BRAND` is
// undefined and that lookup silently answered `de` — which produced exactly
// the defect above, in the browser only (70.13-Runtime-Configuration).
export const countryFor = (brand: DomainKey): MappedCountry | undefined => {
  const countryId = DOMAIN_TO_COUNTRY_ID[brand];
  if (!countryId) {
    return undefined;
  }
  const country = COUNTRIES.find((c) => c.id === countryId);
  if (!country) {
    return undefined;
  }
  const { mapKey } = country;
  if (!mapKey || country.regions.length === 0) {
    return undefined;
  }
  return { ...country, mapKey };
};

export const COUNTRIES: Country[] = [
  {
    id: 'DE',
    name: 'Deutschland',
    mapKey: 'germany',
    regions: [...GERMAN_REGIONS],
  },
  // NB: when uncommenting AT or CH, only set `mapKey` once the matching
  // `public/{mapKey}-states.json` has been added; the widget hides itself while
  // it is absent.
  // {
  //   id: 'AT',
  //   name: 'Österreich',
  //   mapKey: 'austria', // <- only with public/austria-states.json present
  //   regions: [
  //     { id: 'AT-1', name: 'Burgenland', slug: 'burgenland' },
  //     { id: 'AT-2', name: 'Kärnten', slug: 'kaernten' },
  //     { id: 'AT-3', name: 'Niederösterreich', slug: 'niederoesterreich' },
  //     { id: 'AT-4', name: 'Oberösterreich', slug: 'oberoesterreich' },
  //     { id: 'AT-5', name: 'Salzburg', slug: 'salzburg' },
  //     { id: 'AT-6', name: 'Steiermark', slug: 'steiermark' },
  //     { id: 'AT-7', name: 'Tirol', slug: 'tirol' },
  //     { id: 'AT-8', name: 'Vorarlberg', slug: 'vorarlberg' },
  //     { id: 'AT-9', name: 'Wien', slug: 'wien' },
  //   ],
  // },
  // {
  //   id: 'CH',
  //   name: 'Schweiz',
  //   mapKey: 'switzerland', // <- only with public/switzerland-states.json present
  //   regions: [
  //     { id: 'CH-AG', name: 'Aargau', slug: 'aargau' },
  //     { id: 'CH-AI', name: 'Appenzell Innerrhoden', slug: 'appenzell-innerrhoden' },
  //     { id: 'CH-AR', name: 'Appenzell Ausserrhoden', slug: 'appenzell-ausserrhoden' },
  //     { id: 'CH-BE', name: 'Bern', slug: 'bern' },
  //     { id: 'CH-BL', name: 'Basel-Landschaft', slug: 'basel-landschaft' },
  //     { id: 'CH-BS', name: 'Basel-Stadt', slug: 'basel-stadt' },
  //     { id: 'CH-FR', name: 'Freiburg', slug: 'freiburg' },
  //     { id: 'CH-GE', name: 'Genf', slug: 'genf' },
  //     { id: 'CH-GL', name: 'Glarus', slug: 'glarus' },
  //     { id: 'CH-GR', name: 'Graubünden', slug: 'graubuenden' },
  //     { id: 'CH-JU', name: 'Jura', slug: 'jura' },
  //     { id: 'CH-LU', name: 'Luzern', slug: 'luzern' },
  //     { id: 'CH-NE', name: 'Neuenburg', slug: 'neuenburg' },
  //     { id: 'CH-NW', name: 'Nidwalden', slug: 'nidwalden' },
  //     { id: 'CH-OW', name: 'Obwalden', slug: 'obwalden' },
  //     { id: 'CH-SG', name: 'St. Gallen', slug: 'st-gallen' },
  //     { id: 'CH-SH', name: 'Schaffhausen', slug: 'schaffhausen' },
  //     { id: 'CH-SO', name: 'Solothurn', slug: 'solothurn' },
  //     { id: 'CH-SZ', name: 'Schwyz', slug: 'schwyz' },
  //     { id: 'CH-TG', name: 'Thurgau', slug: 'thurgau' },
  //     { id: 'CH-TI', name: 'Tessin', slug: 'tessin' },
  //     { id: 'CH-UR', name: 'Uri', slug: 'uri' },
  //     { id: 'CH-VD', name: 'Waadt', slug: 'waadt' },
  //     { id: 'CH-VS', name: 'Wallis', slug: 'wallis' },
  //     { id: 'CH-ZG', name: 'Zug', slug: 'zug' },
  //     { id: 'CH-ZH', name: 'Zürich', slug: 'zuerich' },
  //   ],
  // },
];

/**
 * The region carrying `slug` — the single source of truth for the slug→name
 * direction that `{state.name}` (30.03.03-Widget-Rendering § Placeholder
 * Variables) and the Federal State breadcrumb (10.02.07 § AC 5) both need.
 *
 * Deliberately **not** brand-scoped: scoping would only matter if two countries
 * shared a region slug, and it costs a domain→country map that is easy to get
 * wrong silently (`institut.erstenachhilfe.de` is a German brand whose key
 * `ien` reads Austrian). Revisit when AT or CH are uncommented.
 *
 * Covers all 16 German regions, not only the 12 with a Federal State Page: the
 * location import tags Standorte in the other four `bundesland:berlin` and
 * friends, so a lookup restricted to the routable 12 would fail on exactly those.
 *
 * An unknown slug returns `undefined` rather than a guess — the result is
 * user-visible text, and a wrong state name is worse than a visible placeholder.
 */
export const regionBySlug = (slug: string): Region | undefined =>
  COUNTRIES.flatMap((country) => country.regions).find(
    (region) => region.slug === slug,
  );
